<script setup>
import { transformMarkedText } from '@/composables/splitSentence';
import VueApexCharts from 'vue3-apexcharts';

const props = defineProps({
  sData: {
    type: Object,
    required: true,
  },
  mobile: {
    type: Boolean,
    default: false,
  },
});

const isAndroid = /android/i.test(navigator.userAgent);

// 생애점수 데이터
const 생애점수 = computed(() => props.sData?.['생애점수']);
const 흐름분석 = computed(() => 생애점수.value?.흐름분석);
const 에너지흐름유형 = computed(() => 흐름분석.value?.패턴명 || '-');
const 에너지흐름설명 = computed(() => 흐름분석.value?.한줄요약 || '');
const 흐름상세분석 = computed(() => 흐름분석.value?.상세설명 || '');
const 대표조언 = computed(() => 흐름분석.value?.조언 || '');

// 시기별 데이터
const 시기카테고리 = ['초년', '청년', '중년', '장년', '노년'];

const 시기목록 = computed(() => {
  if (!생애점수.value) return [];
  return [
    { key: '초년', emoji: 생애점수.value?.초년?.이모지 || '🌱', data: 생애점수.value?.초년 },
    { key: '청년', emoji: 생애점수.value?.청년?.이모지 || '🌿', data: 생애점수.value?.청년 },
    { key: '중년', emoji: 생애점수.value?.중년?.이모지 || '🌳', data: 생애점수.value?.중년 },
    { key: '장년', emoji: 생애점수.value?.장년?.이모지 || '🌾', data: 생애점수.value?.장년 },
    { key: '노년', emoji: 생애점수.value?.노년?.이모지 || '🍂', data: 생애점수.value?.노년 },
  ];
});

// 정점 시기
const 정점시기 = computed(() => {
  if (!생애점수.value) return '';
  for (const 시기 of 시기카테고리) {
    if (생애점수.value?.[시기]?.정점) return 시기;
  }
  return '';
});

const 정점인덱스 = computed(() => 시기카테고리.indexOf(정점시기.value));

// 차트 데이터
const 에너지차트데이터 = computed(() => {
  if (!생애점수.value) return [];
  return 시기카테고리.map((시기) => ({
    key: 시기,
    level: 생애점수.value?.[시기]?.레벨 || 0,
    keyword: 생애점수.value?.[시기]?.키워드 || '',
    isPeak: 생애점수.value?.[시기]?.정점 || false,
  }));
});

const 차트최대값 = computed(() => 흐름분석.value?.차트설정?.y_max || 7);

const 에너지차트시리즈 = computed(() => [
  {
    name: '에너지',
    data: 에너지차트데이터.value.map((item) => item.level),
  },
]);

// PC/모바일 차트 설정 분기
const chartHeight = computed(() => (props.mobile ? 160 : 180));

const 에너지차트옵션 = computed(() => {
  const peakIdx = 정점인덱스.value;
  const keywords = 에너지차트데이터.value.map((item) => item.keyword);
  const isMobile = props.mobile;
  return {
    chart: {
      type: 'line',
      toolbar: { show: false },
      zoom: { enabled: false },
      parentHeightOffset: 0,
      selection: { enabled: false },
      animations: { enabled: false },
    },
    stroke: {
      curve: 'straight',
      width: isMobile ? 4 : 5,
    },
    colors: ['#f27647'],
    markers: {
      discrete: 에너지차트데이터.value.map((_, idx) => ({
        seriesIndex: 0,
        dataPointIndex: idx,
        size: idx === peakIdx ? (isMobile ? 5 : 6) : isMobile ? 3 : 4,
        fillColor: '#fff',
        strokeColor: '#f27647',
      })),
    },
    dataLabels: {
      enabled: true,
      offsetY: -10,
      formatter: (val, { dataPointIndex }) => {
        const kw = keywords[dataPointIndex];
        return kw ? `${kw}` : '';
      },
      style: {
        fontSize: isMobile ? '12px' : '13px',
        fontFamily: 'Pretendard',
        fontWeight: 500,
        colors: ['#757575'],
      },
      background: { enabled: false },
    },
    xaxis: {
      categories: 시기카테고리,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: { show: false },
      tooltip: { enabled: false },
      tickPlacement: 'between',
    },
    yaxis: {
      min: 0,
      max: 차트최대값.value,
      labels: { show: false },
    },
    grid: {
      show: true,
      borderColor: '#ebebeb',
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: isMobile
        ? { left: 0, right: 16, top: -20, bottom: 0 }
        : { left: 0, right: 20, top: -20, bottom: 0 },
    },
    legend: { show: false },
    tooltip: { enabled: false },
    states: {
      hover: { filter: { type: 'none' } },
      active: { filter: { type: 'none' } },
    },
  };
});

// 정점 스타일 DOM 직접 조작
const energyChartRef = ref(null);
const updatePeakStyles = () => {
  nextTick(() => {
    if (!energyChartRef.value) return;
    const peakIdx = 정점인덱스.value;

    const labels = energyChartRef.value.querySelectorAll('.apexcharts-datalabels text');
    labels.forEach((label, idx) => {
      label.setAttribute('fill', idx === peakIdx ? '#f27647' : '#757575');
      label.style.fontWeight = idx === peakIdx ? '800' : '500';
    });

    const circles = energyChartRef.value.querySelectorAll('.apexcharts-series circle');
    circles.forEach((circle, idx) => {
      circle.style.strokeWidth = idx === peakIdx ? '4' : '2';
    });
  });
};
onMounted(updatePeakStyles);
watch([생애점수, 정점인덱스], updatePeakStyles, { deep: true });
</script>

<template>
  <section class="life-cycle" :class="{ 'is-mobile': mobile }">
    <!-- 흐름 타이틀 -->
    <div class="life-cycle__header">
      <p class="life-cycle__type">{{ 에너지흐름유형 }}</p>
      <p class="life-cycle__desc" v-html="transformMarkedText(에너지흐름설명)"></p>
    </div>

    <!-- 차트 -->
    <div class="life-cycle__chart">
      <div ref="energyChartRef" class="chart-container" :class="{ 'no-animation': isAndroid }">
        <VueApexCharts
          type="line"
          :height="chartHeight"
          :options="에너지차트옵션"
          :series="에너지차트시리즈"
        />
      </div>
      <div class="life-cycle__chart-labels">
        <span v-for="(item, idx) in 시기목록" :key="idx" class="life-cycle__chart-label">
          <span class="life-cycle__chart-emoji">{{ item.emoji }}</span>
          <span class="life-cycle__chart-period" :class="{ 'is-peak': item.key === 정점시기 }">
            {{ item.key }}
          </span>
        </span>
      </div>
    </div>

    <!-- 흐름 상세 분석 -->
    <div class="life-cycle__analysis">
      <p class="life-cycle__analysis-title">흐름 상세 분석</p>
      <p class="life-cycle__analysis-desc" v-html="transformMarkedText(흐름상세분석)"></p>
    </div>

    <!-- 시기별 카드 -->
    <div class="life-cycle-period-list">
      <div
        v-for="(period, index) in 시기목록"
        :key="index"
        class="life-cycle-period-card"
        :class="{ 'is-peak': period.key === 정점시기 }"
      >
        <div class="life-cycle-period-card__header">
          <span class="life-cycle-period-card__emoji">{{ period.emoji }}</span>
          <div class="life-cycle-period-card__info">
            <div class="life-cycle-period-card__title-row">
              <span class="life-cycle-period-card__title">{{ period.key }}</span>
              <span v-if="period.data?.키워드" class="life-cycle-period-card__tag">
                {{ period.data.키워드 }}
              </span>
              <span v-if="period.key === 정점시기" class="life-cycle-period-card__peak-tag">
                정점
              </span>
            </div>
            <span class="life-cycle-period-card__period">
              {{ (period.data?.기간 || '').replace(/-/g, ' - ') }}
            </span>
          </div>
        </div>
        <div class="life-cycle-period-card__body">
          <p
            class="life-cycle-period-card__desc"
            v-html="transformMarkedText(period.data?.코멘트)"
          ></p>
        </div>
        <div class="life-cycle-period-card__daeun">
          <div class="life-cycle-period-card__daeun-title">
            <span class="life-cycle-period-card__daeun-name">
              {{ period.data?.대표대운 || '' }}
            </span>
            <span class="life-cycle-period-card__daeun-label">대운</span>
          </div>
          <p v-if="period.data?.대운목록?.length" class="life-cycle-period-card__daeun-flow">
            {{ period.data.대운목록.join(' → ') }}
          </p>
        </div>
      </div>
    </div>

    <!-- 대표 조언 -->
    <div class="life-cycle__advice">
      <p v-html="transformMarkedText(대표조언)"></p>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.life-cycle {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 20px;

  &.is-mobile {
    gap: 16px;
  }

  &__header {
    width: 100%;
  }

  &__type {
    width: 100%;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    color: #575757;
    margin-bottom: 8px;
  }

  &__desc {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: #575757;
  }

  &__chart {
    margin-bottom: 20px;
    width: 100%;
  }

  .chart-container {
    width: 100%;
    overflow: hidden;
    pointer-events: none;

    :deep(.apexcharts-canvas) {
      max-width: 100% !important;
      width: 100% !important;
    }

    :deep(.apexcharts-tooltip) {
      display: none !important;
    }

    :deep(.apexcharts-data-labels text) {
      stroke: white;
      stroke-width: 2px;
      paint-order: stroke;
    }

    :deep(.apexcharts-line-series path) {
      stroke-dasharray: 2000;
      stroke-dashoffset: 2000;
      animation: draw-line 2.5s linear forwards;
    }

    :deep(.apexcharts-series-markers) {
      opacity: 0;
      animation: fade-in 0.5s ease forwards 0.3s;
    }

    :deep(.apexcharts-datalabels) {
      opacity: 0;
      animation: fade-in 0.5s ease forwards 0.3s;
    }

    &.no-animation {
      :deep(.apexcharts-line-series path) {
        stroke-dasharray: none;
        stroke-dashoffset: 0;
        animation: none;
      }

      :deep(.apexcharts-series-markers) {
        opacity: 1;
        animation: none;
      }

      :deep(.apexcharts-datalabels) {
        opacity: 1;
        animation: none;
      }
    }
  }

  @keyframes draw-line {
    to {
      stroke-dashoffset: 0;
    }
  }

  @keyframes fade-in {
    to {
      opacity: 1;
    }
  }

  &__chart-labels {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px 0 12px;
    margin-top: -5px;
  }

  &__chart-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    flex: 1;
  }

  &__chart-emoji {
    font-size: 18px;
  }

  &__chart-period {
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #575757;

    &.is-peak {
      font-weight: 700;
      color: #f27647;
    }
  }

  &__analysis {
    width: 100%;
  }

  &__analysis-title {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    color: #575757;
    margin-bottom: 8px;
  }

  &__analysis-desc {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: #575757;
  }

  &__advice {
    width: 100%;
    padding: 12px 16px;
    border-radius: 8px;
    background: rgba(242, 118, 71, 0.05);

    p {
      font-family: Pretendard;
      font-size: 15px;
      font-weight: 700;
      line-height: 24px;
      color: #f27647;
      margin: 0;
    }
  }

  // 모바일 사이즈 오버라이드
  &.is-mobile {
    .life-cycle__type {
      font-size: 14px;
      line-height: 22px;
      margin-bottom: 6px;
    }

    .life-cycle__desc {
      font-size: 14px;
      line-height: 22px;
    }

    .life-cycle__chart {
      margin-bottom: 16px;
    }

    .life-cycle__chart-labels {
      padding: 0 16px 0 12px;
    }

    .life-cycle__chart-label {
      gap: 3px;
    }

    .life-cycle__chart-emoji {
      font-size: 16px;
    }

    .life-cycle__chart-period {
      font-size: 13px;
      line-height: 18px;
    }

    .life-cycle__analysis-title {
      font-size: 14px;
      line-height: 22px;
      margin-bottom: 6px;
    }

    .life-cycle__analysis-desc {
      font-size: 14px;
      line-height: 22px;
    }

    .life-cycle__advice {
      padding: 10px 14px;

      p {
        font-size: 14px;
        line-height: 22px;
      }
    }
  }
}

.life-cycle-period-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;

  .is-mobile & {
    gap: 16px;
  }
}

.life-cycle-period-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  border: 1px solid #ebebeb;
  border-radius: 12px;

  .is-mobile & {
    gap: 8px;
    padding: 14px;
  }

  &.is-peak {
    border-color: #f27647;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    gap: 10px;

    .is-mobile & {
      gap: 8px;
    }
  }

  &__emoji {
    font-size: 25px;
    padding: 8px;

    .is-mobile & {
      font-size: 22px;
      padding: 6px;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 3px;

    .is-mobile & {
      gap: 2px;
    }
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  &__title {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    color: #333;

    // .is-mobile & {
    //   font-size: 14px;
    //   line-height: 22px;
    // }
  }

  &__tag {
    display: inline-flex;
    padding: 2px 6px;
    border-radius: 4px;
    background: #f8f8f8;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    color: #575757;

    // .is-mobile & {
    //   font-size: 12px;
    //   line-height: 16px;
    // }
  }

  &__peak-tag {
    display: inline-flex;
    padding: 2px 6px;
    border-radius: 4px;
    background: #f27647;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    color: #fff;

    // .is-mobile & {
    //   font-size: 12px;
    //   line-height: 16px;
    // }
  }

  &__period {
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
    color: #575757;
  }

  &__body {
    padding: 8px 10px;
    border-radius: 4px;
    background: #f8f8f8;
  }

  &__desc {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: #575757;
    margin: 0;

    // .is-mobile & {
    //   font-size: 13px;
    //   line-height: 22px;
    // }
  }

  &__daeun {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 8px 10px;
    border-radius: 4px;
    background: #f8f8f8;

    .is-mobile & {
      gap: 4px;
    }
  }

  &__daeun-title {
    display: flex;
    align-items: flex-end;
    gap: 3px;
  }

  &__daeun-name {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 18px;
    color: #333;

    // .is-mobile & {
    //   font-size: 14px;
    // }
  }

  &__daeun-label {
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: #757575;
  }

  &__daeun-flow {
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: #575757;
    margin: 0;
  }
}
</style>
